// Minimal, dependency-free i18n for the web app. The bot stays English.
//
// Adding a language = add one entry to MESSAGES with the same keys as `en`
// (the source-of-truth locale) and add its code to SUPPORTED. Detection:
// localStorage override → navigator.language prefix → fall back to en.
//
// Values may contain HTML (the UI injects them via innerHTML, same as before)
// and {placeholders} filled by t()'s second arg. Keep `en` complete; a missing
// key in another locale transparently falls back to the English string, so a
// partial translation never blanks the UI.

export type Locale = "en" | "pt-BR";

const SUPPORTED: Locale[] = ["en", "pt-BR"];
const STORAGE_KEY = "snail_locale";

/** Resolve the active locale, in priority order:
 *  1. `?lang=` query param (for testing/sharing; also persisted as the override),
 *  2. a previously stored override,
 *  3. the browser language,
 *  4. English. */
export function activeLocale(): Locale {
  // Guard browser globals so t()/formatters are usable in non-browser contexts
  // (e.g. the Node test runner) — defaults to English there.
  if (typeof window === "undefined") return "en";
  const q = new URLSearchParams(window.location.search).get("lang");
  if (q && (SUPPORTED as string[]).includes(q)) {
    localStorage.setItem(STORAGE_KEY, q); // sticky, so it survives the param dropping off
    return q as Locale;
  }
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && (SUPPORTED as string[]).includes(saved)) return saved as Locale;
  const nav = (navigator.language || "en").toLowerCase();
  if (nav.startsWith("pt")) return "pt-BR"; // pt, pt-br, pt-pt → Brazilian copy
  return "en";
}

/** Persist a locale override (e.g. from a language switcher). */
export function setLocale(loc: Locale): void {
  localStorage.setItem(STORAGE_KEY, loc);
}

/** BCP-47 tag for `Intl` date/number formatting in the active locale.
 *  en → en-GB (keeps the existing DD/MM/YYYY, 24h style); pt-BR → pt-BR. */
export function intlLocale(): string {
  return activeLocale() === "pt-BR" ? "pt-BR" : "en-GB";
}

type Catalog = Record<string, string>;

/** Look up `key` in the active locale, falling back to English, then to the
 *  key itself. Fills {name} placeholders from `vars`. */
export function t(key: string, vars?: Record<string, string | number>): string {
  const loc = activeLocale();
  const s = MESSAGES[loc]?.[key] ?? MESSAGES.en[key] ?? key;
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
}

// ---------------------------------------------------------------------------
// Catalogs. `en` is the source of truth; other locales mirror its keys.
// ---------------------------------------------------------------------------

const en: Catalog = {
  // Landing
  "landing.tagline": "Slow correspondence between people you care about.",
  "landing.intro":
    "There's probably someone you've been meaning to write to — one specific " +
    "person. Snail holds the rhythm: two people write to each other, " +
    "one this week, the other next, each letter arriving on its day. (Or a small " +
    "correspondence, taking turns.) Long enough that you have something to say, often " +
    "enough that you don't drift apart. No group-chat tempo, no read receipts, " +
    "no pressure.",
  "landing.rhythm":
    "Letters arrive on a fixed, shared hour — the way mail once came at a known " +
    "time. There's no private countdown that starts when your turn lands; just a " +
    "recurring moment to look forward to. The deadline belongs to the schedule, " +
    "not to you — that's what lets you take your time and still write back. An " +
    "unanswered email feels late; a snail letter is simply waiting for its hour.",
  "landing.how.title": "How it works",
  "landing.how.take_turns":
    "<strong>Take turns.</strong> In a duet you simply alternate; in a larger " +
    "correspondence everyone writes in rotation. Snail lets you know when it's your turn.",
  "landing.how.write":
    "<strong>Write a letter.</strong> Compose at your own pace. " +
    "Text and photos, no submit button.",
  "landing.how.publishes":
    "<strong>It publishes on schedule.</strong> Your letter lands in the group " +
    "on its publication day. Then the turn passes on.",
  "landing.how.keeps_nothing":
    "<strong>snail keeps nothing.</strong> Your letters live in Telegram and " +
    "your inbox — never on our servers.",
  "landing.invite.title": "How to start",
  "landing.invite.body":
    "Start your own correspondence any time — no invite needed. Log in below and " +
    "use \"New correspondence\"; snail sets up the group and you invite whoever you " +
    "like. If you're already on Telegram, messaging the bot with /new does the same " +
    "thing. Already have a letter waiting? Someone can also add you straight to " +
    "their correspondence, or send you a setup link.",
  "landing.invite.email":
    'Questions? Write to <a href="mailto:hello@snailapp.ink">hello@snailapp.ink</a>.',
  "landing.skip_to_login": "Already have a correspondence? Log in →",
  "landing.rule.short": "If it expires in ten minutes, send it in your regular chat.",
  "landing.rule.long": "If it may still matter in years, put it in a letter.",
  "landing.manifesto": "Read the manifesto",
  "landing.signin.title": "Log in with Telegram",
  "landing.signin.note":
    "snail signs you in with your own Telegram account so it can read and write " +
    "your letters directly. There is no snail password to create, and none to " +
    "store — if you use two-factor on Telegram, that code goes to Telegram, never " +
    "to us.",

  // Post-login email ask
  "email_ask.title": "Where should your letters arrive?",
  "email_ask.body":
    "Letters publish in Telegram; with an email on file you also get a copy in your inbox — yours to keep, whatever happens to the apps.",
  "email_ask.placeholder": "you@example.com",
  "email_ask.save": "Save",
  "email_ask.skip": "Not now",
  "email_ask.err_save": "Couldn't save that — try again?",
  "email_ask.welcome_failed":
    "Saved — though we couldn't confirm the delivery email went out. Letters will still arrive there.",

  // Auth form
  "auth.country": "Country",
  "auth.phone_label": "Phone number (without country code)",
  "auth.send_code": "Send code",
  "auth.code_label": "Login code",
  "auth.password_label": "2FA password",
  "auth.sign_in": "Sign in",
  "auth.err_phone": "Enter your phone number without the country code (digits only).",
  "auth.err_login": "Login failed",
  "auth.err_confirm": "Couldn't confirm your identity with the bot — try again.",

  // App shell / sidebar
  "app.correspondences": "Correspondences",
  "app.no_correspondences": "No correspondences yet.",
  "app.load_failed": "Could not load correspondences.",
  "app.select": "Select a correspondence from the sidebar.",
  "app.settings": "Settings",
  "app.details": "{name} settings",
  "app.about": "About",
  "app.logout": "Log out",
  "app.connected": "Connected to snail ✓",
  "app.reconnect.title": "Can't reach Telegram",
  "app.reconnect.body": "You're still signed in — this looks like a connection hiccup.",
  "app.reconnect.retry": "Try again",
  "app.othertab.title": "Open in another tab",
  "app.othertab.body": "snail is already open in another tab or window. To avoid signing you out, only one can be connected at a time. Close the other one, then reload here.",
  "app.othertab.use_here": "Reload",
  "app.your_turn": "✍️ your turn",
  "app.last_published": "Last letter {when}",
  "app.never_published": "No letters yet",
  "app.status.setup": "Setting up",
  "app.status.paused": "Paused",
  "app.status.closed": "Closed",
  "app.new_correspondence": "New correspondence",

  // Personal settings page
  "settings.title": "Settings",
  "settings.loading": "Loading…",
  "settings.load_failed": "Couldn't load your settings.",
  "settings.email_label": "Your email",
  "settings.no_email": "Not set",
  "settings.edit": "Edit",
  "settings.delivery_label": "Email delivery per correspondence",
  "settings.delivery_needs_email": "Add an email above to turn these on.",
  "settings.delivery_save_failed": "Couldn't save that — try again?",
  "settings.logout_all": "Log out everywhere",
  "settings.logout_all_confirm": "Sign out of snail on every device? You'll need to log in again here too.",
  "settings.slot_rounded": "Your timezone is offset by half an hour, and snail stores whole hours only — {picked} will be saved as {actual} your time. Save anyway?",
  "settings.display_name_label": "Display name",
  "settings.language_label": "Language",
  "settings.language_auto": "Auto",

  // Correspondence details page (group-scoped, formerly "Settings")
  "details.publishes": "Publishes",
  "details.cadence": "Cadence",
  "details.members": "Members",
  "details.name": "Name",
  "details.add_email": "add email",

  // About page
  "about.title": "What is snail? 🐌",
  "about.intro":
    "snail is slow, take-turns letter writing — two people, or a small correspondence — " +
    "that lives in a Telegram group. One person writes at a time; everyone else reads.",
  "about.take_turns":
    "<strong>Take turns.</strong> Members rotate. When it's your turn, snail lets " +
    "you know — by email or on Telegram.",
  "about.write":
    "<strong>Write a letter.</strong> Open your correspondence and compose " +
    "below the letters, at your own pace. It's just for your group.",
  "about.publishes":
    "<strong>It publishes on schedule.</strong> Your letter lands in the group on " +
    "the group's publication day. Then the turn passes on.",
  "about.read":
    "<strong>Read everyone.</strong> Published letters collect in the same " +
    "correspondence — newest at the bottom, older mail above.",
  "about.reminders":
    'snail DMs you for turn reminders. We connect that channel for you ' +
    "automatically — nothing to set up. If reminders ever stop, message " +
    '<a href="https://t.me/{bot}" target="_blank">@{bot}</a> once.',
  "about.no_group":
    "Not in a group yet? Ask a founder to add you. Reading and writing appear " +
    "here as soon as you're in one.",

  // Compose / editor
  "compose.paused": "Writing is paused — the group isn't active right now.",
  "compose.someone_writing": "{who} is writing now",
  "compose.not_your_turn_yet": "it's not your turn yet",
  "compose.draft_ahead_banner":
    "✍️ {who} — but you can draft ahead. Your draft publishes when your turn comes.",
  "compose.draft_ahead_banner_dated":
    "✍️ {who} — but you can draft ahead. Your draft goes out {when}, if no one skips.",
  "compose.draft_ahead_current_dated":
    "✍️ {who} — publishing around {current}.",
  "compose.draft_ahead_yours_dated":
    " Draft ahead: your letter goes out {when}, if no one skips.",
  "compose.draft_ahead_yours_undated":
    " Draft ahead: it goes out when your turn comes.",
  "compose.label.your_turn": "✨ Your turn",
  "compose.label.your_draft": "📝 Your draft",
  "compose.turn_banner_dated":
    "✨ It's your turn — your letter publishes {when}. Write below.",
  "compose.turn_banner": "✨ It's your turn — write below. Your letter publishes at the group's next scheduled hour once you've sent something.",
  "compose.skip_turn": "skip this turn",
  "compose.skip_confirm": "Pass your turn to the next writer? I'll hold what you've written for next time.",
  "compose.skip_done": "Passed — your turn is next time.",
  "compose.skip_failed": "Couldn't pass your turn — try again?",
  "compose.sealed_banner": "Sealed — nothing more can be added. It arrives {when}.",
  "compose.sealed_banner_undated": "Sealed — nothing more can be added. It goes out at the next publishing hour.",
  "compose.cancel_letter": "withdraw this letter",
  "compose.cancel_confirm": "Withdraw your sealed letter? Your turn passes and the next writer opens now. Your messages stay in your chat with the bot.",
  "compose.cancel_done": "Withdrawn — your turn has passed.",
  "compose.cancel_failed": "Couldn't withdraw — try again?",
  "compose.write_by": "Write by {when}.",
  "compose.extend_1": "+1 day",
  "compose.extend_3": "+3 days",
  "compose.extend_send": "until it goes out",
  "compose.extend_done": "Okay — write by {when}.",
  "compose.extend_failed": "Couldn't extend — try again?",
  "compose.add_placeholder": "Add to your letter…",
  "compose.draft_placeholder": "Draft ahead…",
  "compose.add": "Add",
  "compose.caption_placeholder": "Caption…",
  "compose.save": "Save",
  "compose.cancel": "Cancel",
  "compose.saved": "saved ✓",
  "compose.err_edit_expired":
    "Couldn't update this old message automatically — delete it and write a new one instead.",
  "compose.err_load": "Couldn't load your letter: {detail}",
  "compose.err_reorder_unresolved": "Couldn't reorder yet — one message is still syncing. Reload and try again.",
  "compose.err_reorder_failed":
    "Your new order didn't reach the server — it will revert unless you retry.",
  "compose.retry": "Retry",
  "compose.err_send": "Send failed: {detail}",
  "compose.err_command": "Commands like {cmd} only work in the Telegram chat with the bot, not here. Open the bot to run it.",
  "compose.err_upload": "Upload failed: {detail}",
  "compose.err_too_long":
    "Telegram messages max out at 4,096 characters — split this into two blocks.",
  "compose.err_file_too_big": "{name} is over Telegram's 2 GB limit and can't be sent.",
  "compose.slow_upload": "Big file — uploading from the browser can take a while.",
  "compose.uploading": "uploading…",
  "compose.upload_failed": "upload failed",

  // Schedule (date-utils)
  "sched.publishes_at_slot": "Publishes at your slot.",
  "sched.publishes_after_first": "Publishes after you write your first message.",
  "sched.publishes_when_turn": "Publishes when your turn comes.",
  "sched.publishes": "Publishes {when}",
  "sched.recurring_unset": "set when the first letter publishes",
  "sched.recurring": "{weekday}s at {time}",
  "sched.label_with_slot": "{label} — publishes {when}",

  // Feed
  "feed.no_chat": "This correspondence has no linked chat yet.",
  "feed.load_failed": "Couldn't load the correspondence. Open it in Telegram.",
  "feed.no_letters": "No letters yet.",
  "feed.next_letter": " · next letter {when}",
  "feed.media_loading": "loading…",
  "feed.media_failed": "couldn't load — tap to retry",
  "feed.download_failed": "Download failed — tap to try again.",
  "feed.video_unsupported":
    "Your browser can't play this video format — downloading it instead.",

  // Setup wizard (founder)
  "setup.claim.title": "Start a new correspondence",
  "setup.claim.intro":
    "Name your correspondence and set its rhythm. You'll create the Telegram group " +
    "and invite people in the next two steps.",
  "setup.name": "Correspondence name",
  "setup.hour": "Publication hour",
  "setup.hour_note": "(your local time)",
  "setup.cadence": "A new letter every…",
  "setup.cadence_1": "week",
  "setup.cadence_n": "{n} weeks",
  "setup.cadence_rec": "{n} weeks — recommended",
  "setup.email": "Your email (optional — letters arrive there too)",
  "setup.create": "Create",
  "setup.err_name_required": "Give the correspondence a name.",
  "setup.err_token_claimed": "This setup link was already used by someone else.",
  "setup.err_token_invalid": "This setup link is invalid or has expired.",
  "setup.err_floodwait": "Telegram asked us to wait {s} seconds — try again then.",
  "setup.err_full": "This correspondence is full.",
  "setup.err_wall":
    "One multi-person correspondence per person for now — message the bot on " +
    "Telegram if you'd like another.",
  "setup.steps.create": "Create the group",
  "setup.steps.invite": "Invite people",
  "setup.step1.title": "Create the group in Telegram",
  "setup.step1.body":
    "snail will create a fresh Telegram group with you in it and add its bot " +
    "as caretaker — the bot watches for joins and carries the letters.",
  "setup.step1.go": "Create the Telegram group",
  "setup.step1.creating": "Creating the group…",
  "setup.step1.promoting": "Adding the snail bot…",
  "setup.step2.title": "Invite people",
  "setup.step2.body":
    "Add people from your contacts, or share a snail link with anyone else. " +
    "They appear below as they join.",
  "setup.link.title": "Share a snail link",
  "setup.link.mint": "Create invite link",
  "setup.link.copy": "Copy",
  "setup.link.copied": "Copied ✓",
  "setup.contacts.title": "Add from your contacts",
  "setup.contacts.load": "Choose from contacts",
  "setup.contacts.loading": "Loading your contacts…",
  "setup.contacts.search": "Search…",
  "setup.contacts.add": "Add selected",
  "setup.contacts.added": "Added ✓",
  "setup.contacts.failed":
    "Couldn't add {names} (their privacy settings) — share the snail link with them instead.",
  "setup.roster.title": "Members",
  "setup.roster.you": "(you)",
  "setup.roster.waiting": "Waiting for members to join…",
  "setup.finalize": "Start the correspondence",
  "setup.finalize.need2": "You need at least one more member to start.",
  "setup.finalize.note": "You write the first letter.",

  // Join page (newcomer)
  "join.title": "You're invited",
  "join.intro":
    "Someone wants you to join their snail correspondence — slow, take-turns letters in a " +
    "quiet Telegram group. One person writes at a time; everyone reads.",
  "join.signin_title": "Sign in with Telegram",
  "join.signin_note":
    "snail signs you in with your own Telegram account so it can add you to " +
    "the group and carry your letters.",
  "join.join": "Join the correspondence",
  "join.joining": "Joining…",
  "join.done.title": "You're in! 🐌",
  "join.done.body":
    "The snail bot will message you when it's your turn to write. Meanwhile " +
    "you can read and draft in the app.",
  "join.open_app": "Open snail",
  "join.err_expired": "This invite link has expired — ask for a fresh one.",
};

const ptBR: Catalog = {
  "landing.tagline": "Correspondência vagarosa entre pessoas de quem você gosta.",
  "landing.intro":
    "Provavelmente há alguém para quem você vem querendo escrever — uma pessoa " +
    "específica. No snail, cartas são mantidas em um ritmo: duas pessoas combinam " +
    "de escrever, uma nesta semana, a outra na próxima, cada carta chegando no seu " +
    "dia. (Ou uma pequena correspondência, se revezando.) Longo o bastante para ter algo a " +
    "dizer, frequente o bastante para vocês não se distanciarem. Sem o ritmo de " +
    "grupo, sem confirmações de leitura, sem pressão.",
  "landing.rhythm":
    "As cartas chegam em um dia e hora fixos e combinados — como o correio chegava " +
    "em hora certa. Não há contagem regressiva que começa quando chega a sua vez; " +
    "apenas um momento recorrente para esperar. O prazo é mantido pelo calendário, " +
    "não por você — é isso que te deixa levar seu tempo e ainda assim responder. " +
    "Um e-mail sem resposta parece atrasado; uma carta do snail está só esperando " +
    "a hora dela.",
  "landing.how.title": "Como funciona",
  "landing.how.take_turns":
    "<strong>Revezem-se.</strong> Numa dupla, vocês simplesmente se alternam; numa " +
    "correspondência maior, todos escrevem em rodízio. O snail avisa quando for a sua vez.",
  "landing.how.write":
    "<strong>Escreva uma carta.</strong> Componha no seu ritmo. " +
    "Texto e fotos, sem botão de enviar.",
  "landing.how.publishes":
    "<strong>Publica no horário.</strong> Sua carta chega ao grupo no dia de " +
    "publicação. Depois a vez passa adiante.",
  "landing.how.keeps_nothing":
    "<strong>O snail não guarda nada.</strong> Suas cartas ficam no Telegram e na " +
    "sua caixa de entrada, nunca nos nossos servidores.",
  "landing.invite.title": "Como começar",
  "landing.invite.body":
    "Comece sua própria correspondência quando quiser, sem precisar de convite. Entre " +
    "abaixo e use \"Nova correspondência\"; o snail cria o grupo e você convida quem " +
    "quiser. Se já usa Telegram, mandar /new para o bot faz a mesma coisa. Já tem uma " +
    "carta esperando? Alguém também pode te adicionar direto à correspondência, ou te " +
    "enviar um link de configuração.",
  "landing.invite.email":
    'Dúvidas? Escreva para <a href="mailto:hello@snailapp.ink">hello@snailapp.ink</a>.',
  "landing.skip_to_login": "Já tem uma correspondência? Entrar →",
  "landing.rule.short": "Se expira em dez minutos, mande na conversa de sempre.",
  "landing.rule.long": "Se ainda pode importar daqui a anos, escreva uma carta.",
  "landing.manifesto": "Leia o manifesto",
  "landing.signin.title": "Entre com o Telegram",
  "landing.signin.note":
    "O snail entra com a sua própria conta do Telegram para poder ler e escrever " +
    "suas cartas diretamente. Não há senha do snail para criar, nem para guardar — " +
    "se você usa verificação em duas etapas no Telegram, esse código vai para o " +
    "Telegram, nunca para nós.",

  "email_ask.title": "Onde suas cartas devem chegar?",
  "email_ask.body":
    "As cartas são publicadas no Telegram; com um email cadastrado você também recebe uma cópia na sua caixa de entrada — sua para guardar, aconteça o que acontecer com os apps.",
  "email_ask.placeholder": "voce@exemplo.com",
  "email_ask.save": "Salvar",
  "email_ask.skip": "Agora não",
  "email_ask.err_save": "Não deu para salvar — tentar de novo?",
  "email_ask.welcome_failed":
    "Salvo — mas não conseguimos confirmar o envio do e-mail de confirmação. As cartas ainda vão chegar lá.",

  "auth.country": "País",
  "auth.phone_label": "Número de telefone (sem o código do país)",
  "auth.send_code": "Enviar código",
  "auth.code_label": "Código de acesso",
  "auth.password_label": "Senha 2FA",
  "auth.sign_in": "Entrar",
  "auth.err_phone": "Digite seu telefone sem o código do país (apenas dígitos).",
  "auth.err_login": "Falha no login",
  "auth.err_confirm": "Não consegui confirmar sua identidade com o bot — tente de novo.",

  "app.correspondences": "Correspondências",
  "app.no_correspondences": "Nenhuma correspondência ainda.",
  "app.load_failed": "Não foi possível carregar as correspondências.",
  "app.select": "Selecione uma correspondência na barra lateral.",
  "app.settings": "Configurações",
  // Colon, not "de {name}": Portuguese contracts de + article (de+os = dos), so
  // "Configurações de Os Primos" is broken grammar — and group names carrying
  // an article ("Os Primos", "A Família") are common. A colon avoids the
  // preposition entirely, so it's correct for every possible name.
  "app.details": "Configurações: {name}",
  "app.about": "Sobre",
  "app.logout": "Sair",
  "app.connected": "Conectado ao snail ✓",
  "app.reconnect.title": "Sem conexão com o Telegram",
  "app.reconnect.body": "Você continua conectado — parece só uma instabilidade de rede.",
  "app.reconnect.retry": "Tentar de novo",
  "app.othertab.title": "Aberto em outra aba",
  "app.othertab.body": "O snail já está aberto em outra aba ou janela. Para não desconectar você, só uma pode ficar conectada por vez. Feche a outra e recarregue aqui.",
  "app.othertab.use_here": "Recarregar",
  "app.your_turn": "✍️ sua vez",
  "app.last_published": "Última carta {when}",
  "app.never_published": "Nenhuma carta ainda",
  "app.status.setup": "Em configuração",
  "app.status.paused": "Pausada",
  "app.status.closed": "Encerrada",
  "app.new_correspondence": "Nova correspondência",

  "settings.title": "Configurações",
  "settings.loading": "Carregando…",
  "settings.load_failed": "Não foi possível carregar suas configurações.",
  "settings.email_label": "Seu e-mail",
  "settings.no_email": "Não definido",
  "settings.edit": "Editar",
  "settings.delivery_label": "Entrega de e-mail por correspondência",
  "settings.delivery_needs_email": "Adicione um e-mail acima para ativar isso.",
  "settings.delivery_save_failed": "Não deu para salvar — tentar de novo?",
  "settings.logout_all": "Sair de todos os dispositivos",
  "settings.logout_all_confirm": "Encerrar sua sessão do snail em todos os dispositivos? Você vai precisar entrar de novo aqui também.",
  "settings.slot_rounded": "Seu fuso horário tem meia hora de diferença, e o snail guarda apenas horas cheias — {picked} será salvo como {actual} no seu horário. Salvar mesmo assim?",
  "settings.display_name_label": "Nome de exibição",
  "settings.language_label": "Idioma",
  "settings.language_auto": "Automático",

  "details.publishes": "Publica",
  "details.cadence": "Cadência",
  "details.members": "Membros",
  "details.name": "Nome",
  "details.add_email": "adicionar e-mail",

  "about.title": "O que é o snail? 🐌",
  "about.intro":
    "O snail é escrita vagarosa de cartas, por turnos — duas pessoas, ou uma " +
    "pequena correspondência — que vive num grupo do Telegram. Uma pessoa escreve por " +
    "vez; as outras leem.",
  "about.take_turns":
    "<strong>Revezem-se.</strong> Os membros se alternam. Quando for a sua vez, o " +
    "snail avisa — por e-mail ou no Telegram.",
  "about.write":
    "<strong>Escreva uma carta.</strong> Abra sua correspondência e componha " +
    "logo abaixo das cartas, no seu ritmo. É só para o seu grupo.",
  "about.publishes":
    "<strong>Publica no horário.</strong> Sua carta chega ao grupo no dia de " +
    "publicação do grupo. Depois a vez passa adiante.",
  "about.read":
    "<strong>Leia todos.</strong> As cartas publicadas ficam na própria " +
    "correspondência — as mais novas embaixo, as antigas acima.",
  "about.reminders":
    "O snail te manda DM com lembretes da sua vez. A gente conecta esse canal " +
    "automaticamente — nada para configurar. Se os lembretes pararem, mande uma " +
    'mensagem para <a href="https://t.me/{bot}" target="_blank">@{bot}</a> uma vez.',
  "about.no_group":
    "Ainda não está numa correspondência snail? Peça a um fundador para te adicionar. Leitura e " +
    "escrita aparecem aqui assim que você entrar em um.",

  "compose.paused": "A escrita está pausada — o grupo não está ativo agora.",
  "compose.someone_writing": "{who} está escrevendo agora",
  "compose.not_your_turn_yet": "ainda não é a sua vez",
  "compose.draft_ahead_banner":
    "✍️ {who} — mas você pode adiantar um rascunho. Ele publica quando chegar a sua vez.",
  "compose.draft_ahead_banner_dated":
    "✍️ {who} — mas você pode adiantar um rascunho. Ele sai {when}, se ninguém pular a vez.",
  "compose.draft_ahead_current_dated":
    "✍️ {who} — publica por volta de {current}.",
  "compose.draft_ahead_yours_dated":
    " Adiante um rascunho: a sua carta sai {when}, se ninguém pular a vez.",
  "compose.draft_ahead_yours_undated":
    " Adiante um rascunho: ele sai quando chegar a sua vez.",
  "compose.label.your_turn": "✨ Sua vez",
  "compose.label.your_draft": "📝 Seu rascunho",
  "compose.turn_banner_dated":
    "✨ É a sua vez — a sua carta é publicada {when}. Escreva abaixo.",
  "compose.turn_banner": "✨ É a sua vez — escreva abaixo. Sua carta sai no próximo horário programado do grupo, assim que você enviar algo.",
  "compose.skip_turn": "pular esta vez",
  "compose.skip_confirm": "Passar sua vez para o próximo? Vou guardar o que você escreveu para a próxima.",
  "compose.skip_done": "Vez passada — a sua volta na próxima.",
  "compose.skip_failed": "Não consegui pular sua vez — tentar de novo?",
  "compose.sealed_banner": "Lacrada — nada mais pode ser acrescentado. Chega {when}.",
  "compose.sealed_banner_undated": "Lacrada — nada mais pode ser acrescentado. Sai na próxima hora de publicação.",
  "compose.cancel_letter": "retirar esta carta",
  "compose.cancel_confirm": "Retirar a sua carta lacrada? A sua vez passa e o próximo escritor começa agora. As suas mensagens continuam na sua conversa com o bot.",
  "compose.cancel_done": "Retirada — a sua vez passou.",
  "compose.cancel_failed": "Não deu para retirar — tenta de novo?",
  "compose.write_by": "Escreva até {when}.",
  "compose.extend_1": "+1 dia",
  "compose.extend_3": "+3 dias",
  "compose.extend_send": "até ela sair",
  "compose.extend_done": "Combinado — escreva até {when}.",
  "compose.extend_failed": "Não deu para estender — tenta de novo?",
  "compose.add_placeholder": "Adicione à sua carta…",
  "compose.draft_placeholder": "Adiante um rascunho…",
  "compose.add": "Adicionar",
  "compose.caption_placeholder": "Legenda…",
  "compose.save": "Salvar",
  "compose.cancel": "Cancelar",
  "compose.saved": "salvo ✓",
  "compose.err_edit_expired":
    "Não consegui atualizar esta mensagem antiga automaticamente — apague-a e escreva uma nova.",
  "compose.err_load": "Não foi possível carregar sua carta: {detail}",
  "compose.err_reorder_unresolved": "Não foi possível reordenar ainda — uma mensagem ainda está sincronizando. Recarregue e tente de novo.",
  "compose.err_reorder_failed":
    "A nova ordem não chegou ao servidor — ela será perdida se você não tentar de novo.",
  "compose.retry": "Tentar de novo",
  "compose.err_send": "Falha ao enviar: {detail}",
  "compose.err_command": "Comandos como {cmd} só funcionam na conversa do Telegram com o bot, não aqui. Abra o bot para executá-lo.",
  "compose.err_upload": "Falha no upload: {detail}",
  "compose.err_too_long":
    "Mensagens do Telegram têm limite de 4.096 caracteres — divida a carta em dois blocos.",
  "compose.err_file_too_big": "{name} passa do limite de 2 GB do Telegram e não pode ser enviado.",
  "compose.slow_upload": "Arquivo grande — o envio pelo navegador pode demorar.",
  "compose.uploading": "enviando…",
  "compose.upload_failed": "falha no upload",

  "sched.publishes_at_slot": "Publica no seu horário.",
  "sched.publishes_after_first": "Publica depois que você escrever sua primeira mensagem.",
  "sched.publishes_when_turn": "Publica quando chegar a sua vez.",
  "sched.publishes": "Publica {when}",
  "sched.recurring_unset": "definido quando a primeira carta publicar",
  "sched.recurring": "{weekday} às {time}",
  "sched.label_with_slot": "{label} — publica {when}",

  "feed.no_chat": "Esta correspondência ainda não tem um chat vinculado.",
  "feed.load_failed": "Não foi possível carregar a correspondência. Abra no Telegram.",
  "feed.no_letters": "Nenhuma carta ainda.",
  "feed.next_letter": " · próxima carta {when}",
  "feed.media_loading": "carregando…",
  "feed.media_failed": "não carregou — toque para tentar de novo",
  "feed.download_failed": "O download falhou — toque para tentar de novo.",
  "feed.video_unsupported":
    "Seu navegador não reproduz este formato de vídeo — baixando o arquivo.",

  "setup.claim.title": "Comece uma nova correspondência",
  "setup.claim.intro":
    "Dê um nome à sua correspondência e defina o ritmo. Você cria o grupo do Telegram " +
    "e convida as pessoas nos dois próximos passos.",
  "setup.name": "Nome da correspondência",
  "setup.hour": "Hora de publicação",
  "setup.hour_note": "(sua hora local)",
  "setup.cadence": "Uma carta nova a cada…",
  "setup.cadence_1": "semana",
  "setup.cadence_n": "{n} semanas",
  "setup.cadence_rec": "{n} semanas — recomendado",
  "setup.email": "Seu e-mail (opcional — as cartas chegam lá também)",
  "setup.create": "Criar",
  "setup.err_name_required": "Dê um nome à correspondência.",
  "setup.err_token_claimed": "Este link de configuração já foi usado por outra pessoa.",
  "setup.err_token_invalid": "Este link de configuração é inválido ou expirou.",
  "setup.err_floodwait": "O Telegram pediu para esperarmos {s} segundos — tente de novo depois.",
  "setup.err_full": "Esta correspondência está cheia.",
  "setup.err_wall":
    "Uma correspondência com várias pessoas por pessoa, por enquanto — mande " +
    "uma mensagem ao bot no Telegram se quiser outra.",
  "setup.steps.create": "Criar o grupo",
  "setup.steps.invite": "Convidar pessoas",
  "setup.step1.title": "Crie o grupo no Telegram",
  "setup.step1.body":
    "O snail vai criar um grupo novo do Telegram com você dentro e adicionar o " +
    "bot como zelador — ele percebe quem entra e carrega as cartas.",
  "setup.step1.go": "Criar o grupo do Telegram",
  "setup.step1.creating": "Criando o grupo…",
  "setup.step1.promoting": "Adicionando o bot do snail…",
  "setup.step2.title": "Convide pessoas",
  "setup.step2.body":
    "Adicione pessoas dos seus contatos, ou compartilhe um link do snail com " +
    "qualquer outra. Elas aparecem abaixo conforme entram.",
  "setup.link.title": "Compartilhe um link do snail",
  "setup.link.mint": "Criar link de convite",
  "setup.link.copy": "Copiar",
  "setup.link.copied": "Copiado ✓",
  "setup.contacts.title": "Adicionar dos seus contatos",
  "setup.contacts.load": "Escolher dos contatos",
  "setup.contacts.loading": "Carregando seus contatos…",
  "setup.contacts.search": "Buscar…",
  "setup.contacts.add": "Adicionar selecionados",
  "setup.contacts.added": "Adicionados ✓",
  "setup.contacts.failed":
    "Não foi possível adicionar {names} (configurações de privacidade) — " +
    "compartilhe o link do snail com essas pessoas.",
  "setup.roster.title": "Membros",
  "setup.roster.you": "(você)",
  "setup.roster.waiting": "Esperando as pessoas entrarem…",
  "setup.finalize": "Começar a correspondência",
  "setup.finalize.need2": "Você precisa de pelo menos mais uma pessoa para começar.",
  "setup.finalize.note": "Você escreve a primeira carta.",

  "join.title": "Você foi convidado",
  "join.intro":
    "Alguém quer você na correspondência dele no snail — cartas vagarosas, " +
    "por turnos, num grupo quieto do Telegram. Uma pessoa escreve por vez; " +
    "todas leem.",
  "join.signin_title": "Entre com o Telegram",
  "join.signin_note":
    "O snail entra com a sua própria conta do Telegram para poder te adicionar " +
    "ao grupo e carregar suas cartas.",
  "join.join": "Entrar na correspondência",
  "join.joining": "Entrando…",
  "join.done.title": "Você entrou! 🐌",
  "join.done.body":
    "O bot do snail vai te avisar quando for a sua vez de escrever. Enquanto " +
    "isso, você pode ler e rascunhar no app.",
  "join.open_app": "Abrir o snail",
  "join.err_expired": "Este convite expirou — peça um novo.",
};

const MESSAGES: Record<Locale, Catalog> = { en, "pt-BR": ptBR };
